import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Sheet,
    Typography,
    Box,
    Button,
    FormControl,
    FormLabel,
    Input,
    Textarea,
    Alert,
    CircularProgress,
    Breadcrumbs,
    Link,
    Divider
} from '@mui/joy';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import domain from '../utils/domain';

interface Section {
    id?: number;
    name: string;
    description: string;
    marks_per_question: number;
}

const EditSection: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const isNewSection = !id;

    const [section, setSection] = useState<Section>({
        name: '',
        description: '',
        marks_per_question: 1
    });

    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!isNewSection) {
            fetchSection();
        }
    }, [id]);

    const fetchSection = async () => {
        try {
            setIsLoading(true);
            const token = localStorage.getItem('adminToken');
            if (!token) {
                navigate('/admin');
                return;
            }

            const response = await fetch(`${domain}/api/admin/sections/${id}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                credentials: 'include'
            });

            if (response.status === 401) {
                localStorage.removeItem('adminToken');
                navigate('/admin');
                return;
            }

            if (!response.ok) {
                throw new Error('Failed to fetch section');
            }

            const data = await response.json();
            setSection(data.section);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load section');
        } finally {
            setIsLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSaving(true);
        setError('');

        try {
            const token = localStorage.getItem('adminToken');
            if (!token) {
                navigate('/admin');
                return;
            }

            const url = isNewSection
                ? `${domain}/api/admin/sections`
                : `${domain}/api/admin/sections/${id}`;

            const response = await fetch(url, {
                method: isNewSection ? 'POST' : 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                credentials: 'include',
                body: JSON.stringify(section)
            });

            if (response.status === 401) {
                localStorage.removeItem('adminToken');
                navigate('/admin');
                return;
            }

            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.message || 'Failed to save section');
            }

            navigate('/admin/dashboard?tab=sections');
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to save section');
        } finally {
            setIsSaving(false);
        }
    };

    const handleInputChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const { name, value } = e.target;
        if (name === 'marks_per_question') {
            const numValue = Number(value);
            if (numValue < 1) return;
            setSection(prev => ({
                ...prev,
                [name]: numValue
            }));
        } else {
            setSection(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ minHeight: '100vh', backgroundColor: 'background.level1' }}>
            <Sheet
                variant="plain"
                sx={{
                    p: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    bgcolor: 'white',
                    borderBottom: '2px solid',
                    borderColor: '#ea7a23',
                    color: '#ea7a23'
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <img
                        src={`${process.env.PUBLIC_URL}/logo-vector.svg`}
                        alt="Vector India Logo"
                        style={{
                            width: '200px',
                            height: 'auto'
                        }}
                    />
                    <Typography level="h4" sx={{ color: '#ea7a23' }}>
                        {isNewSection ? 'Create New Section' : 'Edit Section'}
                    </Typography>
                </Box>
            </Sheet>

            <Box sx={{ p: 2, maxWidth: '800px', margin: '0 auto' }}>
                <Breadcrumbs sx={{ mb: 2 }}>
                    <Link href="/admin/dashboard">Dashboard</Link>
                    <Link href="/admin/dashboard">Sections</Link>
                    <Typography>{isNewSection ? 'Create New Section' : 'Edit Section'}</Typography>
                </Breadcrumbs>

                {error && (
                    <Alert color="danger" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                <Sheet
                    variant="outlined"
                    sx={{
                        borderRadius: 'sm',
                        p: 3,
                        mb: 3
                    }}
                >
                    <form onSubmit={handleSubmit}>
                        <FormControl required sx={{ mb: 2 }}>
                            <FormLabel>Section Name</FormLabel>
                            <Input
                                name="name"
                                value={section.name}
                                onChange={handleInputChange}
                                placeholder="Enter section name"
                            />
                        </FormControl>

                        <FormControl sx={{ mb: 2 }}>
                            <FormLabel>Description</FormLabel>
                            <Textarea
                                name="description"
                                value={section.description}
                                onChange={handleInputChange}
                                minRows={3}
                                placeholder="Enter section description"
                            />
                        </FormControl>

                        <FormControl required sx={{ mb: 3 }}>
                            <FormLabel>Marks per Question</FormLabel>
                            <Input
                                type="number"
                                name="marks_per_question"
                                value={section.marks_per_question}
                                onChange={handleInputChange}
                            />
                        </FormControl>

                        <Divider sx={{ mb: 2 }} />

                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                            <Button
                                variant="outlined"
                                color="neutral"
                                onClick={() => navigate('/admin/dashboard?tab=sections')}
                                startDecorator={<CancelIcon />}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                loading={isSaving}
                                startDecorator={<SaveIcon />}
                            >
                                {isNewSection ? 'Create Section' : 'Save Changes'}
                            </Button>
                        </Box>
                    </form>
                </Sheet>
            </Box>
        </Box>
    );
};

export default EditSection; 